import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import { Toaster } from './components/ui/toaster';
import ProtectedRoute from './components/ProtectedRoute';
import AuthorOrAdminRoute from './components/AuthorOrAdminRoute';
import AdminRoute from './components/AdminRoute';
import AppErrorBoundary from './components/AppErrorBoundary';
import ExpiryBanner from './components/ExpiryBanner';
import { useHeartbeat } from './hooks/useHeartbeat';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Home from './pages/Home';
import BookDetails from './pages/BookDetails';
import UploadBook from './pages/UploadBook';
import UploadAudiobook from './pages/UploadAudiobook';
import Search from './pages/Search';
import Settings from './pages/Settings';
import LandingPage from './pages/LandingPage';
import Contact from './pages/Contact';
import UserManagement from './pages/UserManagement';
import AdminSettings from './pages/AdminSettings';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import About from './pages/About';
import Signup from './pages/Signup';
import MyLibrary from './pages/MyLibrary';
import MyBooks from './pages/MyBooks';
import PayoutBankDetails from './pages/PayoutBankDetails';
import PublicAchievements from './pages/PublicAchievements';
import AuthorProfile from './pages/AuthorProfile';
import PublicLeaderboard from './pages/PublicLeaderboard';
import CommunityChat from './pages/CommunityChat';

// Component to show Landing or Home based on auth
const HomeOrLanding = () => {
  const isAuthenticated = localStorage.getItem('token');
  return isAuthenticated ? <Home /> : <LandingPage />;
};

// Wrapper component to conditionally render Navbar
const AppContent = () => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  
  // Enable heartbeat for presence tracking
  useHeartbeat();
  
  // Re-check auth on route change
  useEffect(() => {
    setIsAuthenticated(!!localStorage.getItem('token'));
  }, [location]);

  // Pages where navbar should be hidden
  const hideNavbarPaths = ['/login', '/welcome', '/contact', '/forgot-password', '/reset-password', '/verify-email'];
  const shouldHideNavbar = hideNavbarPaths.includes(location.pathname) || (!isAuthenticated && location.pathname === '/');

  return (
    <>
      {!shouldHideNavbar && <Navbar />}
      {!shouldHideNavbar && <ExpiryBanner />}
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/welcome" element={<LandingPage />} />
        <Route path="/contact" element={<Contact />} />

        {/* Home - Shows Landing for guests, Home for authenticated users */}
        <Route
          path="/"
          element={<HomeOrLanding />}
        />
        
        <Route
          path="/browse"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/book/:id"
          element={
            <ProtectedRoute>
              <BookDetails />
            </ProtectedRoute>
          }
        />
        
        {/* Admin Only Routes */}
        <Route
          path="/upload"
          element={
            <AdminRoute>
              <UploadBook />
            </AdminRoute>
          }
        />
        
        <Route
          path="/upload-audiobook"
          element={
            <AdminRoute>
              <UploadAudiobook />
            </AdminRoute>
          }
        />
        
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <Search />
            </ProtectedRoute>
          }
        />

        {/* Settings Route */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        {/* Admin User Management */}
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <UserManagement />
            </AdminRoute>
          }
        />

        {/* Admin Settings (Featured Book & Analytics) */}
        <Route
          path="/admin/settings"
          element={
            <AdminRoute>
              <AdminSettings />
            </AdminRoute>
          }
        />

        {/* Author / Admin: My Books dashboard with engagement */}
        <Route
          path="/my-books"
          element={
            <AuthorOrAdminRoute>
              <MyBooks />
            </AuthorOrAdminRoute>
          }
        />

        {/* Author / Admin: payout bank details self-edit */}
        <Route
          path="/me/payout-bank-details"
          element={
            <AuthorOrAdminRoute>
              <PayoutBankDetails />
            </AuthorOrAdminRoute>
          }
        />

        {/* Public Leaderboard */}
        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute>
              <PublicLeaderboard />
            </ProtectedRoute>
          }
        />

        {/* Community Chat */}
        <Route
          path="/community"
          element={
            <ProtectedRoute>
              <CommunityChat />
            </ProtectedRoute>
          }
        />

        {/* My Library - User's bookmarks, notes, reading history */}
        <Route
          path="/library"
          element={
            <ProtectedRoute>
              <MyLibrary />
            </ProtectedRoute>
          }
        />

        {/* Legal Pages - Public */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/about" element={<About />} />
        <Route path="/signup" element={<Signup />} />
        
        {/* Public Achievements Page */}
        <Route path="/achievements/:userId" element={<PublicAchievements />} />
        <Route path="/author/:name" element={<AuthorProfile />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <AppErrorBoundary>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AppErrorBoundary>
    </div>
  );
}

export default App;